/**
 * TasksPanel - the Tasks tab body, used by BOTH the opportunity editor and the
 * project detail. Same component, same data: an opportunity and a project are
 * one row, so its tasks follow it when the bid is won.
 *
 * Completing goes through useCompleteTask: the last open task on an open
 * opportunity asks for the next step first (T-04), after the optional
 * completion note (T-09). A task can name one of the linked site's contacts
 * (T-02) - not on the GLOBAL KSA tab, which has none. The contact's name opens
 * their record: onOpenContact when the screen shows it in place, otherwise in a
 * new browser tab so the editor this panel sits in stays open.
 *
 * Each row names the people tagged on it beside its owner, and says
 * "Tagged" when the signed-in user is on it only because they were tagged.
 */
import { useMemo, useState } from 'react';
import { fmtDate } from '../../utils/format';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useDataScope } from '../../context/DataScope';
import { pickField } from '../../data/crmStages';
import { contactIndex, contactsForAccount, indexById } from '../crm/nextStep';
import TaskComposer, { ContactLink } from './TaskComposer';
import { TaggedChip, TaggedNames } from './TagPeople';
import { taggedNotOwner, taggedOf } from './tagged';
import { useTasks, isOverdue } from './useTasks';
import { useCompleteTask } from './useCompleteTask';
import { canEdit } from '../../data/roles';
import { useMyAccess } from '../../hooks/useMyAccess';

const GREEN = '#1B5E20';
const AMBER = '#B07503';

function TaskRow({ t, editable, onToggle, onDelete, onSave, contacts, contact, onOpenContact, taggedMe }) {
  const done = t.status !== 'open';
  const late = isOverdue(t);
  const [editing, setEditing] = useState(false);

  // Editing reuses the composer, so the @ picker and the required due date
  // behave identically to adding. Changing a task used to mean deleting it and
  // typing it again, which lost who added it and when.
  if (editing) {
    return (
      <div style={{ border: '1px solid #CFE0CF', borderRadius: 8, background: '#F8FAF8', padding: 12 }}>
        <TaskComposer
          compact
          autoFocus
          initial={t}
          contacts={contacts}
          currentContact={contact}
          submitLabel="Save changes"
          onCancel={() => setEditing(false)}
          onSubmit={async (vals) => {
            const ok = await onSave({ id: t.id, ...vals });
            if (ok) setEditing(false);
            return ok;
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 12px',
        border: '1px solid #ECECEC',
        borderRadius: 8,
        background: done ? '#FAFAFA' : '#fff',
      }}
    >
      <button
        type="button"
        disabled={!editable}
        title={done ? 'Reopen' : 'Mark done'}
        onClick={() => onToggle(t)}
        style={{
          flexShrink: 0,
          marginTop: 1,
          width: 17,
          height: 17,
          borderRadius: 4,
          cursor: editable ? 'pointer' : 'default',
          border: `1.5px solid ${done ? GREEN : '#BBB'}`,
          background: done ? GREEN : '#fff',
          color: '#fff',
          fontSize: 11,
          lineHeight: '14px',
          padding: 0,
          fontWeight: 900,
        }}
      >
        {done ? '✓' : ''}
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            color: done ? '#8A8A8A' : '#0D0D0D',
            lineHeight: 1.45,
            textDecoration: done ? 'line-through' : 'none',
            wordBreak: 'break-word',
          }}
        >
          {t.description}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4, fontSize: 10, color: '#888' }}>
          <span style={{ fontWeight: 700, color: done ? '#999' : GREEN }}>{t.owner}</span>
          {taggedMe && <TaggedChip />}
          <TaggedNames names={taggedOf(t)} full style={done ? { color: '#999' } : null} />
          <span style={{ color: late ? AMBER : '#888', fontWeight: late ? 700 : 400 }}>
            due {fmtDate(t.due_date)}
            {late ? ' · overdue' : ''}
          </span>
          {/* the panel sits inside the opportunity editor / project detail: a
              plain link would close it, so without onOpenContact it opens a new tab */}
          {contact && (
            <span>
              with <ContactLink contact={contact} onOpen={onOpenContact} newTab={!onOpenContact} />
            </span>
          )}
          {t.created_by && <span>added by {t.created_by}</span>}
        </div>
      </div>

      {editable && (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            type="button"
            title="Edit task"
            aria-label="Edit task"
            onClick={() => setEditing(true)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: GREEN,
              fontSize: 10,
              fontWeight: 700,
              fontFamily: 'Montserrat',
              padding: '0 2px',
            }}
          >
            Edit
          </button>
          <button
            type="button"
            title="Delete task"
            aria-label="Delete task"
            onClick={() => onDelete(t)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#B4837F',
              fontSize: 10,
              fontWeight: 700,
              fontFamily: 'Montserrat',
              padding: '0 2px',
            }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function TasksPanel({ projectId, entityLabel, editable: hostEditable = true, onHomeUpdated, onOpenContact }) {
  // Tasks are written under the Tasks area wherever they appear (server/roles.js
  // API_AREAS): Tasks View shows them read-only even on an editable record.
  const access = useMyAccess();
  const editable = hostEditable && canEdit(access, 'tasks');
  const { tasks, open, loaded, add, update, setStatus, remove } = useTasks(projectId);
  const [showDone, setShowDone] = useState(false);
  const done = tasks.filter((t) => t.status !== 'open');
  const shown = showDone ? tasks : open;
  const { complete, prompt } = useCompleteTask({ onHomeUpdated });

  // The linked site's contacts (the A-02 link feeds the T-02 picker). Never
  // in KSA scope: its "accounts" are just customer names.
  const scope = useDataScope();
  const { allAccounts, allProjects, users } = useApp();
  const { user } = useAuth();
  const accountId = useMemo(() => {
    const row = (allProjects || []).find((p) => String(p.id) === String(projectId));
    return row ? pickField(row, 'accountId', 'account_id') : null;
  }, [allProjects, projectId]);
  const contacts = useMemo(() => (scope ? [] : contactsForAccount(accountId, indexById(allAccounts))), [scope, accountId, allAccounts]);
  const contactsById = useMemo(() => (scope ? new Map() : contactIndex(allAccounts)), [scope, allAccounts]);

  const toggle = (t) => {
    if (t.status === 'open') complete(t);
    else setStatus(t.id, 'open');
  };
  const del = (t) => {
    if (window.confirm(`Delete this task?\n\n"${t.description}"`)) remove(t.id);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: 'Montserrat' }}>
      {editable && (
        <div style={{ border: '1px solid #E6EDE6', background: '#F8FAF8', borderRadius: 10, padding: 12 }}>
          <TaskComposer contacts={contacts} onSubmit={(t) => add({ ...t, entityLabel })} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#0D0D0D', letterSpacing: 0.4, textTransform: 'uppercase' }}>
          {open.length} open{done.length ? ` · ${done.length} done` : ''}
        </div>
        {done.length > 0 && (
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 10.5,
              fontWeight: 700,
              color: GREEN,
              fontFamily: 'Montserrat',
            }}
          >
            {showDone ? 'Hide completed' : `Show completed (${done.length})`}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {!loaded && <div style={{ fontSize: 11.5, color: '#999' }}>Loading tasks…</div>}
        {loaded && shown.length === 0 && (
          <div
            style={{
              padding: '18px 14px',
              textAlign: 'center',
              color: '#9AA29B',
              fontSize: 11.5,
              border: '1px dashed #E0E4E0',
              borderRadius: 8,
            }}
          >
            No open tasks. Type @ above to assign one.
          </div>
        )}
        {shown.map((t) => (
          <TaskRow
            key={t.id}
            t={t}
            editable={editable}
            onToggle={toggle}
            onDelete={del}
            onSave={update}
            contacts={contacts}
            contact={t.contact_id ? contactsById.get(String(t.contact_id)) : null}
            onOpenContact={onOpenContact}
            taggedMe={taggedNotOwner(t, user, users)}
          />
        ))}
      </div>
      {prompt}
    </div>
  );
}
